/* eslint-disable no-nested-ternary */
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Button, Typography } from '@vartanainc/design-system';
import { HTTP_STATUS_CODES } from '../../constants/common.constants';
import { SLACK_APP_CONSTANTS } from './kiraSlackConstants';
import KiraOrderIcon from '../../assets/KiraSlack/kira_order.png';
import KiraCreditDecisionIcon from '../../assets/KiraSlack/kira_credit_decision.png';
import KiraUpdatesIcon from '../../assets/KiraSlack/kira_updates.png';
import { ReactComponent as Celebration } from '../../assets/celebration.svg';
import { ReactComponent as VartanaLogo } from '../../assets/vartana_logo.svg';
import { ReactComponent as InfoIcon } from '../../assets/info_sky_blue.svg';
import { ReactComponent as WifiOffIcon } from '../../assets/wifi_off.svg';

interface KiraInstallationSuccessProps {
  slackAppId: string | null;
  slackTeamId: string | null;
}

interface KiraInstallationErrorProps {
  statusCode: number;
}

interface KiraSlackInstallationProps {
  className?: string;
}

function KiraInstallationSuccess({
  slackAppId,
  slackTeamId,
}: KiraInstallationSuccessProps): JSX.Element {
  const handleRedirectToSlackApp = (): void => {
    const url = `${SLACK_APP_CONSTANTS.redirection_url}?app=${slackAppId}&team=${slackTeamId}`;
    window.location.href = url;
  };

  return (
    <>
      <Celebration />
      <Typography variant="heading32" className="mt-4">
        {' '}
        Thank You!{' '}
      </Typography>
      <Typography
        variant="heading16"
        color="color-black-100"
        className="my-3 text-center"
      >
        {' '}
        You&apos;re all set - Kira is now live in Slack.{' '}
      </Typography>
      <Button
        className="mt-1 mb-6 !text-base"
        size="large"
        onClick={handleRedirectToSlackApp}
      >
        Go to Kira on Slack
      </Button>
    </>
  );
}

function KiraInstallationDenied(): JSX.Element {
  return (
    <>
      <InfoIcon />
      <Typography variant="heading32" className="mt-4">
        {' '}
        Installation canceled{' '}
      </Typography>
      <Typography
        variant="heading16"
        color="color-black-100"
        className="my-3 text-center"
      >
        {' '}
        If you change your mind, click the button below to add Kira.{' '}
      </Typography>
    </>
  );
}

function KiraInstallationError({ statusCode }: KiraInstallationErrorProps): JSX.Element {
  return (
    <>
      <WifiOffIcon />
      <Typography variant="heading32" className="mt-4">
        {' '}
        Oops!{' '}
      </Typography>
      <Typography
        variant="heading16"
        color="color-black-100"
        className="my-3 text-center"
      >
        {' '}
        {`${
          statusCode === HTTP_STATUS_CODES.UNAUTHORIZED
            ? 'Your workspace is not authorized to install Kira.'
            : 'Looks like something went wrong. Please click the button to try again.'
        }`}
      </Typography>
    </>
  );
}

function handleAddToSlack(): void {
  // disabling this because of clash between eslint and typescript
  // eslint-disable-next-line dot-notation
  const slackClientId = process.env['REACT_APP_SLACK_CLIENT_ID'];
  const url = `${SLACK_APP_CONSTANTS.authorization_url}?client_id=${slackClientId}&scope=${SLACK_APP_CONSTANTS.botScopes}&user_scope=`;
  window.location.href = url;
}

function KiraSlackInstallation({ className }: KiraSlackInstallationProps): JSX.Element {
  const [searchParams] = useSearchParams();
  const statusCode = parseInt(searchParams.get('status') || '0', 10);
  const slackAppId = searchParams.get('appId');
  const slackTeamId = searchParams.get('teamId');
  const installationSuccessful = statusCode === HTTP_STATUS_CODES.SUCCESS;
  const installationCanceled = statusCode === HTTP_STATUS_CODES.FORBIDDEN;
  const selectedComponent = installationSuccessful ? (
    <KiraInstallationSuccess slackAppId={slackAppId} slackTeamId={slackTeamId} />
  ) : installationCanceled ? (
    <KiraInstallationDenied />
  ) : (
    <KiraInstallationError statusCode={statusCode} />
  );

  return (
    <div
      className={twMerge(
        'flex flex-col w-screen h-fit sm:h-screen bg-slate-200',
        className
      )}
    >
      <VartanaLogo className="hidden sm:block ml-16 w-40 h-40" />
      <div className="flex flex-col items-center p-8 bg-transparent h-full">
        <div className="flex flex-col items-center">{selectedComponent}</div>
        {!installationSuccessful && (
          <div>
            <Button
              className="mt-1 mb-7 !text-base"
              size="large"
              onClick={handleAddToSlack}
            >
              Add Kira to Slack
            </Button>
          </div>
        )}
        <div className="flex flex-col items-center bg-white px-12 pt-8 pb-10 mx-20 w-fit mt-5 rounded-lg">
          <Typography variant="heading16" color="color-black-100">
            Take deals to the next level...
          </Typography>
          <div className="flex flex-col sm:flex-row mt-2">
            <div className="flex flex-col items-center">
              <img src={KiraOrderIcon} alt="kiraOrder" className="w-60 h-56" />
              <Typography variant="paragraph14" color="color-black-100" className="mt-4">
                Submit orders directly on slack
              </Typography>
            </div>
            <div className="flex flex-col items-center sm:mx-10">
              <img
                src={KiraCreditDecisionIcon}
                alt="kiraCreditDecision"
                className="w-60 h-56"
              />
              <Typography variant="paragraph14" color="color-black-100" className="mt-4">
                Get credit decision in 15 mins
              </Typography>
            </div>
            <div className="flex flex-col items-center">
              <img src={KiraUpdatesIcon} alt="kiraUpdates" className="w-60 h-56" />
              <Typography variant="paragraph14" color="color-black-100" className="mt-4">
                Stay up to date on your orders
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KiraSlackInstallation;
